import { createElement } from "react";
import { createRoute, lazyRouteComponent } from "@tanstack/react-router";

import { requirePermission } from "../../../../shared/routing/requirePermission";
import {
  COMPANIES_CREATE,
  COMPANIES_UPDATE,
  COMPANIES_VIEW,
} from "../../organizationPermissions";
import type { OrganizationRouteFactory } from "./organizationRouteContext";

const CompanyDetailScreen = lazyRouteComponent(
  () => import("../CompanyDetailScreen"),
  "CompanyDetailScreen",
);
const CompanyFormScreen = lazyRouteComponent(
  () => import("../CompanyFormScreen"),
  "CompanyFormScreen",
);

export const createCompanyDetailRoutes: OrganizationRouteFactory = ({
  context,
  companies,
}) => {
  const editRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${context.basePath}/companies/$id/edit`,
    beforeLoad: requirePermission([COMPANIES_UPDATE]),
    component: function CompanyEditRouteScreen() {
      // Route-scoped params, not a manual pathname split: during the
      // navigate-away transition after a successful update, the global
      // location already points at the new URL while this route's own match
      // (and its params) only change once this component actually unmounts,
      // so it never briefly re-renders with the wrong id. basePath is a
      // runtime string, so the path template loses the literal type
      // TanStack Router needs to type params -- $id is guaranteed by the
      // route path itself.
      const { id } = editRoute.useParams() as unknown as { id: string };
      return createElement(CompanyFormScreen, {
        repository: companies,
        basePath: context.basePath,
        companyId: id,
      });
    },
  });

  const detailRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${context.basePath}/companies/$id`,
    beforeLoad: requirePermission([COMPANIES_VIEW]),
    component: function CompanyDetailRouteScreen() {
      const { id } = detailRoute.useParams() as unknown as { id: string };
      return createElement(CompanyDetailScreen, {
        repository: companies,
        basePath: context.basePath,
        companyId: id,
      });
    },
  });

  return [
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${context.basePath}/companies/create`,
      beforeLoad: requirePermission([COMPANIES_CREATE]),
      component: () =>
        createElement(CompanyFormScreen, {
          repository: companies,
          basePath: context.basePath,
        }),
    }),
    editRoute,
    detailRoute,
  ];
};
